import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useUser } from '@/lib/auth';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { GoogleOAuthUnauthorizedError } from '../../google/errors';
import { useAlertDialog } from '../../hooks/useAlertDialog';
import { ClassroomClient } from '../ClassroomClient';
import { googleClassroomContextManager } from '../GoogleClassroomContextManager';
import { TeacherClient } from '../TeacherClient';
import { useAuthErrorDialog } from './useAuthErrorDialog';
import { useInvalidAccessErrorDialog } from '.';
export type GoogleClassroomCreateAssignmentDialogProps = {
  quizSet: Pick<QuizSetDetailsRes, 'title' | 'entryMapHashId'>;
  onCreateSuccess?: () => void;
  title?: string;
};
export function useCreateAssignmentDialog() {
  const {
    t
  } = useTranslation(['common']);
  const alertDialog = useAlertDialog();
  const authErrorDialog = useAuthErrorDialog();
  const invalidAccessErrorDialog = useInvalidAccessErrorDialog();
  const router = useRouter();
  const {
    logOut
  } = useUser();
  const open = ({
    title = t('googleClassroom.createAssignmentDialog.title') ?? '',
    quizSet,
    onCreateSuccess
  }: GoogleClassroomCreateAssignmentDialogProps) => {
    overlay.open(({
      isOpen,
      close
    }) => {
      return <ConfirmDialog title={title} content={`[${quizSet.title}]`} open={isOpen} onClose={close} onConfirm={async () => {
        const oauthToken = googleClassroomContextManager.getGoogleOAuthToken();
        const context = googleClassroomContextManager.getLandingPageContext();
        if (oauthToken == null || context == null) {
          void invalidAccessErrorDialog.open();
          ClassroomClient.closeIframe();
          void router.push('/landing');
          return;
        }
        try {
          await TeacherClient.createAssignment({
            quizSet: quizSet,
            options: {
              oauthToken,
              context
            }
          });
          onCreateSuccess?.();
          setTimeout(() => {
            close();
            ClassroomClient.closeIframe();
          }, 300);
        } catch (error) {
          if (error instanceof GoogleOAuthUnauthorizedError) {
            close();
            await authErrorDialog.open();
            void logOut({
              redirectTo: `/login?callbackUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`
            });
            return;
          }
          console.error(error);
          await alertDialog.open({
            title: t('googleClassroom.invalidTeacherAccountError.title'),
            content: t('googleClassroom.invalidTeacherAccountError.content')
          });
        }
      }} />;
    });
  };
  return {
    open
  };
}
export const DIALOG_OPEN_QUERY_PARAM_KEY = 'shouldShowCreateAssignmentDialog';